/** @format */

import { MessageCircle, X } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../../hooks/useToast";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { Toast } from "../../components/ui/Toast";

interface Props {
  sopId: string;
  sopTitle: string;
  versionId: string | undefined;
  stepIndex: number;
  candidateId?: string;
  fieldLabel: string;
}

const FORM_WIDTH = 288;
const FORM_HEIGHT_APPROX = 220;
const GAP = 6;

export const FieldFeedbackButton: FC<Props> = ({
  sopId,
  sopTitle,
  versionId,
  stepIndex,
  candidateId,
  fieldLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [formPos, setFormPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [comment, setComment] = useState("");
  const { submitting, submitFeedback } = useFeedbackStore();
  const { toast, showToast, hideToast } = useToast();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setComment("");
  }, []);

  const handleOpen = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= FORM_HEIGHT_APPROX + GAP
        ? rect.bottom + GAP
        : rect.top - FORM_HEIGHT_APPROX - GAP;
    const left = Math.min(rect.left, window.innerWidth - FORM_WIDTH - 12);
    setFormPos({ top, left });
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => textareaRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    const onDown = (e: MouseEvent) => {
      if (
        formRef.current?.contains(e.target as Node) ||
        buttonRef.current?.contains(e.target as Node)
      )
        return;
      close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, close]);

  const handleSubmit = async () => {
    if (!comment.trim() || !versionId) return;
    try {
      close();
      await submitFeedback(sopId, {
        sopTitle,
        versionId,
        stepIndex,
        comment: comment.trim(),
        selectedText: fieldLabel,
        ...(candidateId ? { candidateId } : {}),
      });
      showToast("Feedback submitted", "", "success");
    } catch {
      showToast("Failed to submit feedback", "Please try again.", "error");
    }
  };

  if (!versionId) return null;

  return (
    <>
      {createPortal(
        <Toast
          open={toast.open}
          onOpenChange={(o) => !o && hideToast()}
          title={toast.title ?? ""}
          description={toast.description}
          type={toast.type}
          duration={toast.duration}
        />,
        document.body,
      )}

      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={[
          "inline-flex items-center justify-center rounded p-1",
          "text-slate-400 border border-transparent",
          "hover:text-fuchsia-300 hover:border-fuchsia-500/40 hover:bg-slate-700/50",
          "transition-all duration-150",
        ].join(" ")}
        title={`Add feedback for ${fieldLabel}`}
      >
        <MessageCircle className="h-3.5 w-3.5 shrink-0" />
      </button>

      {open &&
        createPortal(
          <div
            ref={formRef}
            style={{
              position: "fixed",
              top: formPos.top,
              left: formPos.left,
              width: FORM_WIDTH,
              zIndex: 9999,
            }}
            className="rounded-xl border border-slate-600/80 bg-slate-900/95 shadow-2xl shadow-black/60 backdrop-blur"
          >
            <div className="flex items-center justify-between border-b border-slate-700/70 px-3.5 py-2.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500/25 to-pink-500/25 border border-purple-500/20">
                  <MessageCircle className="h-3.5 w-3.5 text-fuchsia-200" />
                </span>
                <span className="truncate max-w-[160px]">{fieldLabel}</span>
              </div>
              <button
                onClick={close}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200 transition-colors"
                aria-label="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="p-3 space-y-2.5">
              <textarea
                ref={textareaRef}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What should be improved?"
                rows={3}
                className={[
                  "w-full resize-none rounded border px-3 py-2 text-sm",
                  "border-slate-700 bg-slate-950/40 text-slate-100 placeholder-slate-500",
                  "focus:border-fuchsia-500/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/15",
                ].join(" ")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={submitting || !comment.trim()}
                className={[
                  "w-full rounded px-3 py-1.5 text-sm font-semibold text-white",
                  "bg-gradient-to-r from-purple-600 to-pink-600",
                  "hover:from-purple-500 hover:to-pink-500",
                  "shadow-lg shadow-purple-500/15",
                  "disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none",
                  "transition-all",
                ].join(" ")}
              >
                {submitting ? "Sending…" : "Send feedback"}
              </button>
              <p className="text-[11px] text-slate-500">
                <span className="font-semibold text-slate-400">⌘/Ctrl</span>
                {" + "}
                <span className="font-semibold text-slate-400">Enter</span>
                {" to send."}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
